import { Link, useNavigate } from "react-router-dom";
import { User, ChevronDown, LogOut, Settings } from "lucide-react";
import { Button, DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger, Tooltip, TooltipContent, TooltipTrigger, useSidebar } from "@ncoder/ui";
import { useAuth } from "@/hooks/auth/useAuth";
import { auditLog } from "@/utils/audit-logger";
import toast from "react-hot-toast";

interface UserProfileProps {
  readonly user?: {
    readonly name: string;
    readonly email: string;
    readonly avatar?: string;
  };
  readonly variant?: "sidebar" | "topnav"; // Add variant for different layouts
}

/**
 * User profile component for sidebar and top navigation
 */
export function UserProfile({
  user: propUser,
  variant = "sidebar",
}: UserProfileProps) {
  const navigate = useNavigate();
  // Only use sidebar context when variant is "sidebar"
  const sidebarContext = variant === "sidebar" ? useSidebar() : null;
  const isCollapsed = sidebarContext?.state === "collapsed";
  const { user: authUser, logout } = useAuth();

  // Use auth user data if available, otherwise fall back to prop user or defaults
  const user = authUser ||
    propUser || {
      name: "User",
      email: "user@company.com",
      avatar: "/placeholder.svg",
    };

  const handleLogout = async () => {
    try {
      console.log('🚪 User Profile: Initiating logout...');
      auditLog.log("USER_LOGOUT_INITIATED");
      
      // Show loading toast
      const toastId = toast.loading('Logging out...', {
        icon: '🔄',
      });
      
      // Perform logout
      await logout();
      
      // Update toast to success
      toast.success('✅ Logged out successfully', {
        id: toastId,
        duration: 2000,
      });
      
      console.log('✅ Logout successful, redirecting to login...');
      auditLog.log("USER_LOGOUT_SUCCESS");
      
      // Redirect to login page
      setTimeout(() => {
        navigate('/auth/login', { replace: true });
      }, 500);
      
    } catch (error) {
      console.error("❌ Error during logout:", error);
      auditLog.log("USER_LOGOUT_ERROR", {
        error: error instanceof Error ? error.message : "Unknown error",
      });
      
      // Show error toast
      toast.error('Failed to logout. Please try again.', {
        duration: 4000,
      });
      
      // Fallback: Force redirect to login even if logout fails
      setTimeout(() => {
        window.location.href = "/auth/login";
      }, 1000);
    }
  };

  // Top navigation variant - compact icon only
  if (variant === "topnav") {
    return (
      <DropdownMenu>
        <Tooltip>
          <TooltipTrigger asChild>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0 rounded-full hover:bg-accent"
              >
                <div className="h-6 w-6 rounded-full bg-primary/10 flex items-center justify-center">
                  <User className="h-3 w-3 text-primary" />
                </div>
              </Button>
            </DropdownMenuTrigger>
          </TooltipTrigger>
          <TooltipContent>
            <p>{user.name}</p>
          </TooltipContent>
        </Tooltip>
        <DropdownMenuContent align="end" className="w-56 z-50">
          <div className="px-2 py-1.5 text-sm">
            <p className="font-medium">{user.name}</p>
            <p className="text-xs text-muted-foreground truncate">
              {user.email}
            </p>
          </div>
          <DropdownMenuSeparator />
          {/* <DropdownMenuItem asChild>
            <Link to="/profile" className="flex items-center gap-2">
              <User className="h-4 w-4" />
              Profile Settings
            </Link>
          </DropdownMenuItem> */}
          <DropdownMenuItem asChild>
            <Link to="/org/connection" className="flex items-center gap-2">
              <Settings className="h-4 w-4" />
              Org Settings
            </Link>
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onClick={handleLogout}
            className="flex items-center gap-2 text-destructive"
          >
            <LogOut className="h-4 w-4" />
            Logout
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  // Original sidebar variants
  if (isCollapsed) {
    return (
      <div className="flex items-center justify-center w-full">
        <Tooltip>
          <TooltipTrigger asChild>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  className="h-10 w-10 p-0 rounded-lg flex items-center justify-center"
                >
                  <User className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start">
                <DropdownMenuItem asChild>
                  <Link to="/profile" className="flex items-center gap-2">
                    <User className="h-4 w-4" />
                    Profile
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={handleLogout}
                  className="flex items-center gap-2 text-destructive"
                >
                  <LogOut className="h-4 w-4" />
                  Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </TooltipTrigger>
          <TooltipContent side="right">
            <p>{user.name}</p>
          </TooltipContent>
        </Tooltip>
      </div>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" className="w-full justify-start p-2 h-auto">
          <div className="flex items-center gap-2 w-full">
            <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
              <User className="h-4 w-4 text-primary" />
            </div>
            <div className="flex-1 text-left">
              <p className="text-sm font-medium truncate">{user.name}</p>
              <p className="text-xs text-muted-foreground truncate">
                {user.email}
              </p>
            </div>
            <ChevronDown className="h-4 w-4 text-muted-foreground" />
          </div>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-56">
        <DropdownMenuItem asChild>
          <Link to="/profile" className="flex items-center gap-2">
            <User className="h-4 w-4" />
            Profile Settings
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <Link to="/org/connection" className="flex items-center gap-2">
            <Settings className="h-4 w-4" />
            Org Settings
          </Link>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={handleLogout}
          className="flex items-center gap-2 text-destructive"
        >
          <LogOut className="h-4 w-4" />
          Logout
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
